import { Injectable } from '@angular/core';
import { Model, Offer, Series } from '@shared/graphql/generated';
import { CarItem } from '@shared/models/car-item';


@Injectable({
  providedIn: 'root'
})
export class CarItemTransformService {
  transformOffer(offer: Offer): CarItem {
    const {
      first_registration,
      mileage,
      model,
      additional_info,
      current_price_final,
      initial_price_final,
      average_price_final,
      created_at,
    } = offer;
    const { series } = model;
    const { brand } = series;
    const sortByIdAsc = (a, b) => +a.id - +b.id;
    const sortedPhotos: string[] = [ ...offer.photos ].sort(sortByIdAsc).map(i => i.image.url);
    const {
      battery_capacity,
      recharge_mileage,
      total_electric_torque,
    } = model.additional_properties;
    const dayAgo = new Date();
    const createdAt = new Date(created_at);

    dayAgo.setDate(dayAgo.getDate() - 1);

    return {
      id: offer.id,
      isNew: false,
      heading: [ brand.name, series.name ].filter(v => !!v).join(' '),
      subHeading: [ model.name, `(${model.year})` ].filter(v => !!v).join(' '),
      brand: {
        id: brand.id,
        name: brand.name,
        logo: brand.logo.url,
      },
      price: current_price_final.usd,
      image: sortedPhotos[0],
      specs: [
        {
          label: $localize`Пробіг`,
          value: mileage ? `${(mileage * 10000).toFixed(0)} ${$localize`км`}` : null,
        },
        {
          label: $localize`Первинна реєстрація`,
          value: first_registration ? (new Date(first_registration)).toLocaleDateString('uk-UA', {
            day: undefined,
            month: 'long',
            year: 'numeric'
          }) : null,
        },
        {
          label: $localize`Ємність батареї`,
          value: battery_capacity ? `${battery_capacity} ${$localize`кВт/год`}` : null,
        },
        {
          label: $localize`Крутний момент`,
          value: total_electric_torque ? `${total_electric_torque} ${$localize`(Н·м)`}` : null,
        },
        {
          label: $localize`Запас ходу NEDC (CLTC)`,
          value: recharge_mileage ? `${recharge_mileage} ${$localize`км`}` : null,
        },
      ],
      modelIds: [ offer.model.id ],
      labels: [
        ...(additional_info.report ? [ {
          color: '#50ad5d',
          icon: 'sax:bulk-verify',
          text: $localize`Звіт`
        } ] : []),
        ...(createdAt > dayAgo ? [ {
          color: '#875bf7',
          icon: 'sax:bulk-flash',
          text: $localize`Свіжа`,
        } ] : []),
      ],
      ...(average_price_final && {
        averagePriceDiff: {
          usd: current_price_final.usd - average_price_final.usd,
          uah: current_price_final.uah - average_price_final.uah,
        },
      }),
      ...(initial_price_final && {
        initialPriceDiff: {
          usd: current_price_final.usd - initial_price_final.usd,
          uah: current_price_final.uah - initial_price_final.uah,
        },
      }),
    };
  }

  transformSeries(series: Series): CarItem {
    const prices = series.models.reduce((acc: number[], model: Model) => ([
      ...acc,
      ...(model.price_final ? [ model.price_final.usd ] : []),
      ...model.offers.map((offer: Offer) => offer.current_price_final.usd),
    ]), []);
    const minPrice = prices.length ? Math.min(...prices) : null;
    const hasBiggerPrice = prices.length ? prices.some(price => price > minPrice) : false;
    const getAllModelProperties = name => series.models.map((model: Model) => model.additional_properties[name]).filter(v => !!v);
    const batteryCapacities: number[] = getAllModelProperties('battery_capacity');
    const maxTorques: number[] = getAllModelProperties('total_electric_torque');
    const rechargeMileages: number[] = getAllModelProperties('recharge_mileage');

    return {
      id: series.id,
      isNew: true,
      heading: [ series.brand.name, series.name ].filter(v => !!v).join(' '),
      brand: {
        id: series.brand.id,
        name: series.brand.name,
        logo: series.brand.logo.url,
      },
      price: minPrice,
      hasBiggerPrice,
      image: series.preview.url,
      specs: [
        {
          label: $localize`Ємність батареї`,
          value: batteryCapacities.length ? `${Math.max(...batteryCapacities)} ${$localize`кВт/год`}` : null,
        },
        {
          label: $localize`Крутний момент`,
          value: maxTorques.length ? `${Math.max(...maxTorques)} ${$localize`(Н·м)`}` : null,
        },
        {
          label: $localize`Запас ходу NEDC (CLTC)`,
          value: rechargeMileages.length ? `${Math.max(...rechargeMileages)} ${$localize`км`}` : null,
        },
      ],
      modelIds: series.models.map((model: Model) => model.id),
      labels: [],
    };
  }
}
